/**
 * Settings → Usage (DOP-280).
 *
 * One firm-level bar. Credits only (the contract's unit — 1 credit = 1,000
 * tokens): no dollars, no input/output split, no model names — what a firm buys
 * is an allowance, and the moment a settings page shows the cost behind it the
 * conversation becomes about margin instead of usage.
 *
 * Renders NOTHING at all when the pool is not configured or the request fails.
 * A usage bar is not load-bearing, and an error card in its place would be a
 * worse settings page than no section: a deployment that never bought a pool has
 * no percentage to show, and 0% would be a claim rather than an absence.
 */

import React, { useEffect, useState } from "react";

import { getUsageQuota, UsageQuota } from "../../services/usageService";
import SettingsSection from "./SettingsSection";

/**
 * Credits are the CONTRACT's unit (1 credit = 1,000 tokens, from the backend's
 * tokens_per_credit) — the settings page must read like the order form, so
 * amounts render as credits, never raw tokens.
 *
 * 128,600 → "128,600"; only past a million does it compact: 2,500,000 → "2.5M".
 * Credit counts live in the tens of thousands, so full digits with separators
 * are the honest, readable default.
 */
export const formatCredits = (credits: number): string => {
	const safe = Math.max(0, Math.round(credits));
	if (safe >= 1_000_000_000) {
		return `${Number((safe / 1_000_000_000).toFixed(1))}B`;
	}
	if (safe >= 1_000_000) {
		return `${Number((safe / 1_000_000).toFixed(1))}M`;
	}
	return safe.toLocaleString("en-US");
};

/** Fallback when the backend predates tokens_per_credit in the payload. */
const DEFAULT_TOKENS_PER_CREDIT = 1_000;

/**
 * Epoch ms → "1 September 2026", the date the allowance goes back to full.
 *
 * Formatted in UTC, not the browser's zone (DOP-280). The period boundary IS a
 * UTC midnight — the backend anchors it there — so rendering it locally turns
 * 1 September into 31 August for every user west of UTC, and a firm reading
 * "Resets 31 August" on 31 August has been told the wrong thing. The date is a
 * billing-calendar fact, not a local instant.
 */
export const formatResetDate = (epochMs: number): string =>
	new Date(epochMs).toLocaleDateString(undefined, {
		year: "numeric",
		month: "long",
		day: "numeric",
		timeZone: "UTC",
	});

/**
 * Three states, by severity. The thresholds are on the TRUE percentage, not the
 * capped bar width, so a firm at 140% is red rather than merely full.
 */
const barColor = (pct: number): string => {
	if (pct >= 1) return "bg-red-500";
	if (pct >= 0.8) return "bg-amber-500";
	return "bg-[#008782]";
};

const labelColor = (pct: number): string => {
	if (pct >= 1) return "text-red-600";
	if (pct >= 0.8) return "text-amber-600";
	return "text-text-base";
};

const UsageSection: React.FC = () => {
	const [quota, setQuota] = useState<UsageQuota | null>(null);

	useEffect(() => {
		let cancelled = false;
		getUsageQuota()
			.then((result) => {
				if (!cancelled) setQuota(result);
			})
			// Swallowed on purpose: see the file docstring. There is nothing the
			// user can do about it and nothing they lose by not seeing it.
			.catch(() => {
				if (!cancelled) setQuota(null);
			});
		return () => {
			cancelled = true;
		};
	}, []);

	if (!quota || !quota.enabled) return null;

	const pool = quota.pool_tokens ?? 0;
	const used = quota.used_tokens ?? 0;
	if (pool <= 0) return null;

	const tokensPerCredit =
		quota.tokens_per_credit && quota.tokens_per_credit > 0
			? quota.tokens_per_credit
			: DEFAULT_TOKENS_PER_CREDIT;
	const usedCredits = used / tokensPerCredit;
	const poolCredits = pool / tokensPerCredit;
	// Work Doppler's engineers did for the firm (DOP-561) is folded into
	// `used` by the backend and is NOT called out here (DOP-572): it is
	// covered by the firm's contract, so a line naming it reads as a charge
	// the firm did not agree to. `implementation_tokens` stays on the API for
	// operators.

	const pct = quota.pct_used ?? used / pool;
	// The bar stops at full; the number beside it does not.
	const barWidth = Math.min(100, Math.max(0, pct * 100));
	const wholePercent = Math.round(pct * 100);

	return (
		<SettingsSection
			title="Usage"
			description="Your firm's shared allowance for this billing period. Everyone on your account draws from the same pool."
		>
			<div className="flex items-baseline justify-between">
				<span className={`text-base font-medium ${labelColor(pct)}`}>
					{wholePercent}% used
				</span>
				<span className="text-sm text-text-muted">
					{formatCredits(usedCredits)} of {formatCredits(poolCredits)} credits
					used
				</span>
			</div>

			<div
				className="mt-2 h-2 w-full overflow-hidden rounded-full bg-gray-200"
				role="progressbar"
				aria-label="Credits used this period"
				aria-valuemin={0}
				aria-valuemax={100}
				aria-valuenow={wholePercent}
			>
				<div
					className={`h-full rounded-full ${barColor(pct)}`}
					// The only permitted inline style on this page: a bar's width IS
					// the datum, and Tailwind has no utility for an arbitrary runtime
					// percentage.
					style={{ width: `${barWidth}%` }}
				/>
			</div>

			{quota.period_end != null && (
				<p className="mt-2 text-sm text-text-muted">
					Resets {formatResetDate(quota.period_end)}
				</p>
			)}
		</SettingsSection>
	);
};

export default UsageSection;
